/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * Component that creates a table
 * required params:
 *  - data
 *  Example : 
[
    ["Job Sap", "WST-Sap", "SAPJS"],
    ["Job name 1", "Workstation 1", "JS1"],
    ["Job name 2", "Workstation 2", "JS2"],
    ["Job name 3", "Workstation 3", "JS3"],
    ["Job name 4", "Workstation 4", "JS4"],
]
 */

import React, { useState, Fragment } from "react";
import PropTypes from "prop-types";
import ImageButton from "./ImageButton";
import ScrollBar from "./ScrollBar";
import Text from "./Text";
import EventHandlersManager from "../../controllers/EventHandlersManager";

function Table({
    data,
    sort = false,
    sortFunction,
    backgroundColor = "#262626",
    hoverRowColor,
    headerColor = "#363636",
    headerFontColor = "#f2f2f2",
    fontColor = "#c6c6c6",
    width,
    height,
    onRowClick,
    isSortingUp,
    textSize,
    textSizeDecimal,
    minRows = 4,
    isReloadingEnabled,
    aframeClasses,
    micStates,
    inVRMode,
    disabledList,
    ...otherAttributes
}) {
  
    // fill the table with elements
    const createRow = (value, index) => {
        let hasReactElement = false;
        let currentPosition = 0;

        return value.map((v, i) => {
            const isFunction = v instanceof Function;
            hasReactElement = hasReactElement || isFunction;
            let currentElementWidth = ELEMENT_WIDTH;
            if (hasReactElement) {
                currentElementWidth = isFunction
                    ? currentElementWidth / 2
                    : (width - ELEMENT_WIDTH / 2) / (TABLE_GRID_COLUMNS - 1);
            }
            const x =
                currentPosition +
                currentElementWidth / 2 -
                width / 2 -
                (isScrollingEnabled && !index ? width / 32 : 0);
            const elem = isFunction ? (
                v(currentElementWidth, ELEMENT_HEIGHT)
            ) : v.toString().includes("mic") ? (
                <>
                    <ImageButton
                        key={i}
                        aframeClasses={aframeClasses}
                        position={!inVRMode ? `-0.22 0.002 0.001` : "-0.53 0.0085 0.005"}
                        width={width * 0.05}
                        height={width * 0.05}
                        disabled={true}
                        src={v}
                    />
                </>
            ) : (
                <Text
                    align={"center"}
                    color={!index ? headerFontColor : fontColor}
                    text={v.toString()}
                    width={
                        !index ? currentElementWidth * 0.7 : currentElementWidth
                    }
                    crop
                    cropLines={ELEMENT_HEIGHT * 0.8}
                    sizeDecimal={
                        textSizeDecimal
                            ? !index
                                ? textSizeDecimal
                                : textSizeDecimal * 1.1
                            : 14 / currentElementWidth
                    }
                    position={"0 0.005 0"}
                />
            );
            currentPosition += currentElementWidth;

            return (
                <a-entity
                    key={index * value.length + i}
                    position={`${x} 0 ${width / 200}`}
                >
                    {elem}
                    {sort && !isFunction && !index && (
                        <ImageButton
                            aframeClasses={aframeClasses}
                            position={`${
                                currentElementWidth / 2 - width * 0.03
                            } 0 0`}
                            width={width * 0.05}
                            height={width * 0.05}
                            onClickHandler={() => {
                                sortFunction(i, isSortingUp[i]);
                            }}
                            src={
                                isSortingUp[i] === "up"
                                    ? "#SortingUp"
                                    : "#SortingDown"
                            }
                        />
                    )}
                </a-entity>
            );
        });
    };

    const createTable = () => {
        const startIndex =
            Math.round(percentage * (data.length - MAX_ROWS)) + 1;
        const drawableData = isScrollingEnabled
            ? [data[0], ...data.slice(startIndex, startIndex + MAX_ROWS - 1)]
            : data;

        return drawableData.map((value, index) => {
            const y = index * ELEMENT_HEIGHT + ELEMENT_HEIGHT / 2 - height / 2;
            const animation = onRowClick && (!disabledList || disabledList[index] === "available")
                ? {
                      animation__mouseenter_color: `property: color; startEvents: mouseenter; to: ${
                          hoverRowColor || headerColor
                      }; dur:150; enabled:${index !== 0}`,
                      animation__mouseleave_color: `property: color; startEvents: mouseleave; to: ${backgroundColor}; dur:150; enabled:${
                          index !== 0
                      }`,
                      animation__mouseenter_position: `property: position; startEvents: mouseenter; to: 0 ${-y} ${
                          height / 20
                      }; dur:150; enabled:${index !== 0}`,
                      animation__mouseleave_position: `property: position; startEvents: mouseleave; to: 0 ${-y} ${
                          width / 200
                      }; dur:150; enabled:${index !== 0}`
                  }
                : null;
            return (
                <a-plane
                    id={value[0]}
                    key={"plane" + index * value.length}
                    position={`${
                        isScrollingEnabled && !index ? width / 32 : 0
                    } ${-y} ${width / 200}`}
                    color={!index ? headerColor : backgroundColor}
                    width={
                        width -
                        2 * TABLE_PADDING +
                        (isScrollingEnabled && !index ? width / 16 : 0)
                    }
                    height={ELEMENT_HEIGHT - 2 * TABLE_PADDING}
                    class={aframeClasses}
                    onClick={
                        index && onRowClick && (!disabledList || disabledList[index] === "available") ? 
                            () => onRowClick(startIndex + index - 2, value) 
                        : null
                    }
                    {...animation}
                >
                    {createRow(value, index)}
                </a-plane>
            );
        });
    };

    const [percentage, setPercentage] = useState(0);
    const focused = React.useRef(false);
    const root = React.useRef();
    const MAX_ROWS = 6;
    const TABLE_GRID_ROWS = data.length;
    const TABLE_GRID_COLUMNS = data[0].length;
    const isScrollingEnabled = TABLE_GRID_ROWS > MAX_ROWS;
    if (!width) width = 16;
    if (!height) height = 9;
    if (isScrollingEnabled) width = (width / 16) * 15;
    const ELEMENT_WIDTH = width / TABLE_GRID_COLUMNS;
    let ELEMENT_HEIGHT = height / TABLE_GRID_ROWS;
    if (ELEMENT_HEIGHT > height / minRows) ELEMENT_HEIGHT = height / minRows;
    if (ELEMENT_HEIGHT < height / 6) ELEMENT_HEIGHT = height / 6;
    const TABLE_PADDING = height / 500;

    const events = {
        mouseenter: {
            element: root,
            isReactRef: true,
            callback: (e) => {
                focused.current = true;
            }
        },
        mouseleave: {
            element: root,
            isReactRef: true,
            callback: (e) => {
                focused.current = false;
            }
        },
        wheel: (e) => {
            if (!focused.current) return;
            setPercentage((old) => {
                const delta = 1 / TABLE_GRID_ROWS;
                if (e.deltaY > 0 && old < 1) return old + delta;
                else if (e.deltaY < 0 && old > 0)
                    return old - delta > 0 ? old - delta : 0;
                else return old;
            });
        }
    };

    return (
        <a-entity ref={root} class="Table" {...otherAttributes}>
            <a-plane
                width={width}
                height={height}
                color={headerColor}
                position={`${isScrollingEnabled ? -width / 32 : 0} 0 0`}
            >
                {createTable()}
            </a-plane>
            {isScrollingEnabled && (
                <>
                    <EventHandlersManager
                        reloadable={isReloadingEnabled}
                        events={events}
                        enabled
                    />
                    <ScrollBar
                        width={width / 16}
                        height={height - ELEMENT_HEIGHT}
                        position={`${width / 2} ${-ELEMENT_HEIGHT / 2} ${
                            width / 200
                        }`}
                        percentage={percentage}
                        setPercentage={setPercentage}
                        aframeClasses={aframeClasses}
                        windowContentsRatio={MAX_ROWS / TABLE_GRID_ROWS}
                    />
                </>
            )}
        </a-entity>
    );
}

Table.propTypes = {
    data: PropTypes.array.isRequired
};

export default Table;
